import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CartActions } from '@spartacus/cart/base/core';
import { CheckoutCostCenterConnector } from '@spartacus/checkout/b2b/core';
import { CheckoutDeliveryAddressConnector, CheckoutDeliveryModesConnector, CheckoutPaymentConnector } from '@spartacus/checkout/base/core';
import { CheckoutState } from '@spartacus/checkout/base/root';
import {
  AuthActions,
  GlobalMessageActions,
  GlobalMessageService,
  GlobalMessageType,
  LoggerService,
  OCC_USER_ID_ANONYMOUS,
  SiteContextActions,
  TranslationService,
  UserActions,
  UserAddressService,
  WindowRef,
  tryNormalizeHttpError,
  withdrawOn
} from '@spartacus/core';
import { OrderConnector } from '@spartacus/order/core';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { CustomCheckoutConnector } from '../../connectors/custom-checkout.connector';
import { CustomCheckoutActions } from '../actions';

@Injectable()
export class CheckoutEffects {
  protected logger = inject(LoggerService);
  private contextChange$ = this.actions$.pipe(ofType(SiteContextActions.CURRENCY_CHANGE, SiteContextActions.LANGUAGE_CHANGE));

  addDeliveryAddress$: Observable<UserActions.LoadUserAddresses | CustomCheckoutActions.SetDeliveryAddress | CustomCheckoutActions.AddDeliveryAddressFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.ADD_DELIVERY_ADDRESS),
      map((action: CustomCheckoutActions.AddDeliveryAddress) => action.payload),
      mergeMap((payload) =>
        this.checkoutDeliveryAddressConnector.createAddress(payload.userId, payload.cartId, payload.address).pipe(
          mergeMap((address) => {
            address.titleCode = payload.address.titleCode;
            if (payload.address.region && payload.address.region.isocode) {
              Object.assign(address.region, {
                isocode: payload.address.region.isocode
              });
            }
            if (payload.userId === OCC_USER_ID_ANONYMOUS) {
              return [
                new CustomCheckoutActions.SetDeliveryAddress({
                  userId: payload.userId,
                  cartId: payload.cartId,
                  address
                })
              ];
            } else {
              return [
                new UserActions.LoadUserAddresses(payload.userId),
                new CustomCheckoutActions.SetDeliveryAddress({
                  userId: payload.userId,
                  cartId: payload.cartId,
                  address
                })
              ];
            }
          }),
          catchError((error) => of(new CustomCheckoutActions.AddDeliveryAddressFail(tryNormalizeHttpError(error, this.logger))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  setDeliveryAddress$: Observable<CustomCheckoutActions.SetDeliveryAddressSuccess | CustomCheckoutActions.SetDeliveryAddressFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.SET_DELIVERY_ADDRESS),
      map((action: any) => action.payload),
      mergeMap((payload) => {
        return this.checkoutDeliveryAddressConnector.setAddress(payload.userId, payload.cartId, payload.address.id).pipe(
          mergeMap(() => [new CustomCheckoutActions.SetDeliveryAddressSuccess(payload.address)]),
          catchError((error) => of(new CustomCheckoutActions.SetDeliveryAddressFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  updateUserAddress$: Observable<
    | UserActions.LoadUserAddresses
    | CustomCheckoutActions.UpdateUserAddressSuccess
    | CustomCheckoutActions.SetDeliveryAddress
    | CartActions.LoadCart
    | CustomCheckoutActions.UpdateUserAddressFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.UPDATE_USER_ADDRESS),
      map((action: CustomCheckoutActions.UpdateUserAddress) => action.payload),
      mergeMap((payload) => {
        return this.checkoutConnector.update(payload.userId, payload.addressId, payload.address).pipe(
          switchMap((data) => {
            return [
              new CustomCheckoutActions.UpdateUserAddressSuccess(data),
              new CustomCheckoutActions.SetDeliveryAddress({
                userId: payload.userId,
                cartId: payload.cartId,
                address: data
              })
            ];
          }),
          catchError((error) => of(new CustomCheckoutActions.UpdateUserAddressFail(tryNormalizeHttpError(error, this.logger))))
        );
      })
    )
  );

  updateUserAddressAddressBook$: Observable<CustomCheckoutActions.UpdateUserAddressBookSuccess | CustomCheckoutActions.UpdateUserAddressBookFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.UPDATE_USER_ADDRESS),
      map((action: CustomCheckoutActions.UpdateUserAddressBook) => action.payload),
      mergeMap((payload) => {
        return this.checkoutConnector.update(payload.userId, payload.addressId, payload.address).pipe(
          switchMap((data) => {
            return [new CustomCheckoutActions.UpdateUserAddressBookSuccess(data)];
          }),
          catchError((error) => of(new CustomCheckoutActions.UpdateUserAddressBookFail(tryNormalizeHttpError(error, this.logger))))
        );
      })
    )
  );

  /*   @Effect({ dispatch: false }) */
  showGlobalMessageOnUpdateSuccess$ = this.actions$.pipe(
    ofType(CustomCheckoutActions.UPDATE_USER_ADDRESS_SUCCESS),
    tap(() => {
      this.loadAddresses();
      this.showGlobalMessage('addressForm.userAddressUpdateSuccess');
    })
  );

  loadSupportedDeliveryModes$: Observable<CustomCheckoutActions.LoadSupportedDeliveryModesSuccess | CustomCheckoutActions.LoadSupportedDeliveryModesFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.LOAD_SUPPORTED_DELIVERY_MODES),
      map((action: any) => action.payload),
      mergeMap((payload) => {
        return this.checkoutDeliveryModesConnector.getSupportedModes(payload.userId, payload.cartId).pipe(
          map((data) => {
            return new CustomCheckoutActions.LoadSupportedDeliveryModesSuccess(data);
          }),
          catchError((error) => of(new CustomCheckoutActions.LoadSupportedDeliveryModesFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  clearCheckoutMiscsDataOnLanguageChange$: Observable<
    CustomCheckoutActions.CheckoutClearMiscsData | CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess | CustomCheckoutActions.ResetGetPaymentTypesProcess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteContextActions.LANGUAGE_CHANGE),
      mergeMap(() => [
        new CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess(),
        new CustomCheckoutActions.ResetGetPaymentTypesProcess(),
        new CustomCheckoutActions.CheckoutClearMiscsData()
      ])
    )
  );

  clearDeliveryModesOnCurrencyChange$: Observable<CustomCheckoutActions.ClearSupportedDeliveryModes> = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteContextActions.CURRENCY_CHANGE),
      map(() => new CustomCheckoutActions.ClearSupportedDeliveryModes())
    )
  );

  clearCheckoutDataOnLogin$: Observable<
    | CustomCheckoutActions.ClearCheckoutData
    | CustomCheckoutActions.ResetSetDeliveryAddressProcess
    | CustomCheckoutActions.ResetSetDeliveryModeProcess
    | CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGIN),
      mergeMap(() => [
        new CustomCheckoutActions.ClearCheckoutData(),
        new CustomCheckoutActions.ResetSetDeliveryAddressProcess(),
        new CustomCheckoutActions.ResetSetDeliveryModeProcess(),
        new CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess()
      ])
    )
  );

  clearCheckoutDataOnLogout$: Observable<
    | CustomCheckoutActions.ClearCheckoutData
    | CustomCheckoutActions.ResetSetDeliveryAddressProcess
    | CustomCheckoutActions.ResetSetDeliveryModeProcess
    | CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGOUT),
      mergeMap(() => [
        new CustomCheckoutActions.ClearCheckoutData(),
        new CustomCheckoutActions.ResetSetDeliveryAddressProcess(),
        new CustomCheckoutActions.ResetSetDeliveryModeProcess(),
        new CustomCheckoutActions.ResetLoadSupportedDeliveryModesProcess()
      ])
    )
  );

  setDeliveryMode$: Observable<CustomCheckoutActions.SetDeliveryModeSuccess | CustomCheckoutActions.SetDeliveryModeFail | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.SET_DELIVERY_MODE),
      map((action: any) => action.payload),
      mergeMap((payload) => {
        return this.checkoutDeliveryModesConnector.setMode(payload.userId, payload.cartId, payload.selectedModeId).pipe(
          mergeMap(() => {
            return [
              new CustomCheckoutActions.SetDeliveryModeSuccess(payload.selectedModeId),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartId: payload.cartId
              })
            ];
          }),
          catchError((error) => of(new CustomCheckoutActions.SetDeliveryModeFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  createPaymentDetails$: Observable<
    UserActions.LoadUserPaymentMethods | CartActions.LoadCart | CustomCheckoutActions.CreatePaymentDetailsSuccess | CustomCheckoutActions.CreatePaymentDetailsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.CREATE_PAYMENT_DETAILS),
      map((action: any) => action.payload),
      mergeMap((payload) => {
        // get information for creating a subscription directly with payment provider
        return this.checkoutPaymentConnector.createPaymentDetails(payload.userId, payload.cartId, payload.paymentDetails).pipe(
          mergeMap((details) => {
            if (payload.userId === OCC_USER_ID_ANONYMOUS) {
              this.winRef.localStorage?.setItem('billingChecked', 'true');
              return [
                new CustomCheckoutActions.CreatePaymentDetailsSuccess(details),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ];
            } else {
              this.winRef.localStorage?.setItem('billingChecked', 'true');
              return [
                new UserActions.LoadUserPaymentMethods(payload.userId),
                new CustomCheckoutActions.CreatePaymentDetailsSuccess(details),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ];
            }
          }),
          catchError((error) => of(new CustomCheckoutActions.CreatePaymentDetailsFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  setPaymentDetails$: Observable<CustomCheckoutActions.SetPaymentDetailsSuccess | CustomCheckoutActions.SetPaymentDetailsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.SET_PAYMENT_DETAILS),
      map((action: any) => action.payload),
      mergeMap((payload) => {
        return this.checkoutPaymentConnector.setPaymentDetails(payload.userId, payload.cartId, payload.paymentDetails.id).pipe(
          map(() => new CustomCheckoutActions.SetPaymentDetailsSuccess(payload.paymentDetails)),
          catchError((error) => of(new CustomCheckoutActions.SetPaymentDetailsFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  placeOrder$: Observable<CustomCheckoutActions.PlaceOrderSuccess | GlobalMessageActions.AddMessage | CustomCheckoutActions.PlaceOrderFail | CartActions.RemoveCart> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.PLACE_ORDER),
        map((action: any) => action.payload),
        mergeMap((payload) => {
          return this.orderConnector.placeOrder(payload.userId, payload.cartId, payload.termsChecked).pipe(
            switchMap((data) => [new CartActions.RemoveCart({ cartId: payload.cartId }), new CustomCheckoutActions.PlaceOrderSuccess(data)]),
            catchError((error) => of(new CustomCheckoutActions.PlaceOrderFail(tryNormalizeHttpError(error, this.logger))))
          );
        }),
        withdrawOn(this.contextChange$)
      )
  );

  LoadCheckoutState$: Observable<CustomCheckoutActions.LoadCheckoutStateSuccess | CustomCheckoutActions.LoadCheckoutStateFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.LOAD_CHECKOUT_DETAILS),
      map((action: CustomCheckoutActions.LoadCheckoutState) => action.payload),
      mergeMap((payload) => {
        return this.checkoutConnector.getCheckoutDetails(payload.userId, payload.cartId).pipe(
          map((data: CheckoutState) => new CustomCheckoutActions.LoadCheckoutStateSuccess(data)),
          catchError((error) => of(new CustomCheckoutActions.LoadCheckoutStateFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  reloadDetailsOnMergeCart$: Observable<CustomCheckoutActions.LoadCheckoutState> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.MERGE_CART_SUCCESS),
      map((action: CartActions.MergeCartSuccess) => action.payload),
      map((payload) => {
        return new CustomCheckoutActions.LoadCheckoutState({
          userId: payload.userId,
          cartId: payload.cartId
        });
      })
    )
  );

  clearCheckoutDeliveryAddress$: Observable<CustomCheckoutActions.ClearCheckoutDeliveryAddressFail | CustomCheckoutActions.ClearCheckoutDeliveryAddressSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.CLEAR_CHECKOUT_DELIVERY_ADDRESS),
      map((action: CustomCheckoutActions.ClearCheckoutDeliveryAddress) => action.payload),
      filter((payload) => Boolean(payload.cartId)),
      switchMap((payload) => {
        return this.checkoutDeliveryAddressConnector.clearCheckoutDeliveryAddress(payload.userId, payload.cartId).pipe(
          map(() => new CustomCheckoutActions.ClearCheckoutDeliveryAddressSuccess()),
          catchError((error) => of(new CustomCheckoutActions.ClearCheckoutDeliveryAddressFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  clearCheckoutDeliveryMode$: Observable<CustomCheckoutActions.ClearCheckoutDeliveryModeFail | CustomCheckoutActions.ClearCheckoutDeliveryModeSuccess | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.CLEAR_CHECKOUT_DELIVERY_MODE),
        map((action: CustomCheckoutActions.ClearCheckoutDeliveryMode) => action.payload),
        filter((payload) => Boolean(payload.cartId)),
        concatMap((payload) => {
          return this.checkoutDeliveryModesConnector.clearCheckoutDeliveryMode(payload.userId, payload.cartId).pipe(
            map(
              () =>
                new CustomCheckoutActions.ClearCheckoutDeliveryModeSuccess({
                  ...payload
                })
            ),
            catchError((error) =>
              from([
                new CustomCheckoutActions.ClearCheckoutDeliveryModeFail({
                  ...payload,
                  error: tryNormalizeHttpError(error, this.logger)
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ])
            )
          );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  setCostCenter$: Observable<CustomCheckoutActions.SetCostCenterSuccess | CustomCheckoutActions.SetCostCenterFail | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.SET_COST_CENTER),
      map((action: CustomCheckoutActions.SetCostCenter) => action.payload),
      switchMap((payload) => {
        return this.checkoutCostCenterConnector.setCostCenter(payload.userId, payload.cartId, payload.costCenterId).pipe(
          mergeMap((_data) => [
            new CartActions.LoadCart({
              cartId: payload.cartId,
              userId: payload.userId
            }),
            new CustomCheckoutActions.SetCostCenterSuccess(payload.costCenterId)
          ]),
          catchError((error) => of(new CustomCheckoutActions.SetCostCenterFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  loadRedsysPayment$: Observable<CustomCheckoutActions.LoadRedsysPaymentSuccess | GlobalMessageActions.AddMessage | CustomCheckoutActions.LoadRedsysPaymentFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.LOAD_REDSYS_PAYMENT),
        map((action: any) => action.payload),
        mergeMap(({ userId, cartId, saveCard }) => {
          return this.checkoutConnector.loadRedsysPayment(userId, cartId, saveCard).pipe(
            switchMap((data) => [new CustomCheckoutActions.LoadRedsysPaymentSuccess(data)]),
            catchError((error) => of(new CustomCheckoutActions.LoadRedsysPaymentFail(tryNormalizeHttpError(error, this.logger))))
          );
        }),
        withdrawOn(this.contextChange$)
      )
  );

  loadSequrapayment$: Observable<CustomCheckoutActions.LoadSequraPaymentSuccess | GlobalMessageActions.AddMessage | CustomCheckoutActions.LoadSequraPaymentFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.LOAD_SEQURA_PAYMENT),
        map((action: any) => action.payload),
        mergeMap(({ userId, cartId, paymentId }) => {
          return this.checkoutConnector.loadSequraPayment(userId, cartId, paymentId).pipe(
            switchMap((data) => [new CustomCheckoutActions.LoadSequraPaymentSuccess(data)]),
            catchError((error) => of(new CustomCheckoutActions.LoadSequraPaymentFail(tryNormalizeHttpError(error, this.logger))))
          );
        }),
        withdrawOn(this.contextChange$)
      )
  );

  loadPaypalPayment$: Observable<
    CustomCheckoutActions.LoadPaypalPaymentSuccess | GlobalMessageActions.AddMessage | CartActions.LoadCart | CustomCheckoutActions.LoadPaypalPaymentFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.LOAD_PAYPAL_PAYMENT),
      map((action: any) => action.payload),
      mergeMap(({ userId, cartId, paymentId }) => {
        return this.checkoutConnector.loadPaypalPayment(userId, cartId, paymentId).pipe(
          switchMap((data) => [new CustomCheckoutActions.LoadPaypalPaymentSuccess(data)]),
          catchError((error) => of(new CustomCheckoutActions.LoadPaypalPaymentFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  createPaypalOrder$: Observable<
    CustomCheckoutActions.CreatePaypalOrderSuccess | GlobalMessageActions.AddMessage | CartActions.RemoveCart | CartActions.LoadCart | CustomCheckoutActions.CreatePaypalOrderFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomCheckoutActions.CREATE_PAYPAL_ORDER),
      map((action: any) => action.payload),
      mergeMap(({ userId, cartId, paymentId, token, PayerID }) => {
        return this.checkoutConnector.createPaypalOrder(userId, cartId, paymentId, token, PayerID).pipe(
          switchMap((data) => [new CustomCheckoutActions.CreatePaypalOrderSuccess(data), new CartActions.RemoveCart({ cartId })]),
          catchError((error) => of(new CustomCheckoutActions.CreatePaypalOrderFail(tryNormalizeHttpError(error, this.logger))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private checkoutPaymentConnector: CheckoutPaymentConnector,
    protected translation: TranslationService,
    private checkoutCostCenterConnector: CheckoutCostCenterConnector,
    private checkoutConnector: CustomCheckoutConnector,
    private userAddressService: UserAddressService,
    private messageService: GlobalMessageService,
    private checkoutDeliveryModesConnector: CheckoutDeliveryModesConnector,
    private checkoutDeliveryAddressConnector: CheckoutDeliveryAddressConnector,
    private orderConnector: OrderConnector,
    protected winRef: WindowRef
  ) {}

  /**
   * Show global confirmation message with provided text
   */
  private showGlobalMessage(text: string): void {
    this.messageService.add({ key: text }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
  }

  private loadAddresses(): void {
    this.userAddressService.loadAddresses();
  }
}
